/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2026-06-04.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "le-numerique-planoise-visite-chantier-2026",
  "title": "Le Numérique à Planoise : deux étages debout, livraison visée mi-2027",
  "excerpt": "Visite de chantier le 2 juin 2026 rue du Luxembourg : deux étages sur quatre montés, façades à l'été, livraison mi-2027. Reste à savoir qui remplira les 2 280 m² locatifs des quatre étages.",
  "seoTitle": "Le Numérique à Planoise : livraison mi-2027",
  "seoDescription": "Visite du chantier du bâtiment Le Numérique rue du Luxembourg : deux étages sur quatre montés, 2 280 m² de bureaux à louer et une livraison visée mi-2027.",
  "category": "collectivites",
  "tags": [
    "Le Numérique",
    "Planoise",
    "Territoire 25",
    "Grand Besançon Métropole",
    "French Tech"
  ],
  "author": "jimmy",
  "publishedAt": "2026-06-04",
  "updatedAt": "2026-06-04",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-vue-generale-du-quartier-de-planoise.jpg",
    "credit": "Photo : Besançon, vue générale du quartier de Planoise — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Vue générale du quartier de Planoise à Besançon depuis la colline de Rosemont : longues barres d'immeubles blanches, zone d'activités et grues de chantier au second plan, collines boisées à l'horizon"
  },
  "content": [
    {
      "type": "lead",
      "text": "Grand Besançon Métropole et Territoire 25 ont ouvert le chantier du bâtiment **Le Numérique** à la presse mardi 2 juin 2026, à l'angle de la rue du Luxembourg et de la rue du Piémont, à Planoise. Deux étages sur quatre se dressent déjà et le calendrier reste inchangé : livraison à la mi-2027, accueil annoncé pour l'automne suivant. Reste la question la plus concrète pour l'écosystème tech bisontin : qui occupera les 2 280 m² locatifs de ses quatre étages, et selon quelles règles ?"
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Quoi",
          "value": "« Le Numérique », tiers-lieu et immeuble de bureaux de 3 072 m² dédié à la filière numérique"
        },
        {
          "label": "Quand",
          "value": "Visite de chantier le 2 juin 2026 ; livraison mi-2027, accueil annoncé à l'automne 2027"
        },
        {
          "label": "Où",
          "value": "9-11 rue du Luxembourg, angle rue du Piémont, quartier de Planoise, Besançon"
        },
        {
          "label": "Qui",
          "value": "Grand Besançon Métropole et sa SPL Territoire 25 ; architecte Jean-Baptiste Barbet"
        },
        {
          "label": "Pour qui",
          "value": "Entreprises et startups du numérique, étudiants, associations, habitants du quartier"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Deux étages sur quatre, et un chantier annoncé « dans les temps »"
    },
    {
      "type": "p",
      "text": "Au croisement de la rue du Luxembourg, seuls deux des quatre étages que comptera le bâtiment, en plus de son rez-de-chaussée, étaient montés le jour de la visite[^1]. Le dossier remis à la presse décrit un état d'avancement plus fin : maquette BIM terminée, pieux et fondations achevés, élévation en cours[^2]. Le permis de construire avait été obtenu en mars 2025, le chantier démarré en novembre de la même année[^2]."
    },
    {
      "type": "p",
      "text": "L'architecte Jean-Baptiste Barbet a qualifié le chantier de « dans les temps » : le gros œuvre doit être achevé à la mi-juillet et les façades posées à partir du mois d'août[^1]. Le phasage officiel du maître d'ouvrage situe le démarrage des façades à ossature bois sur la fenêtre juin-juillet 2026[^2] : l'écart se compte en semaines, pas en trimestres. La livraison, elle, reste fixée à la mi-2027 par le calendrier du maître d'ouvrage[^2] — « juin 2027 », précise le compte rendu de la visite[^1] — un horizon que la métropole affichait déjà en novembre 2023, sous la forme d'une ouverture « mi-2027 »[^4] ; l'accueil des entreprises, étudiants, habitants, associations et structures de formation est annoncé dès l'automne 2027[^1]."
    },
    {
      "type": "p",
      "text": "Deux chiffres circulent, qui ne mesurent pas la même chose : le coût constructible avoisine les sept millions d'euros, dont 25 % pour le seul lot chauffage-ventilation[^1], quand le coût total de l'opération atteint 10,5 M€ HT, financés par l'ANRU (1,51 M€), l'ANRU+ au titre de « Planoise, quartier d'excellence numérique » (1,21 M€), la Région Bourgogne-Franche-Comté (2,83 M€), Grand Besançon Métropole (2 M€) et un prêt renouvellement urbain de la Banque des Territoires (2,32 M€)[^2][^5]."
    },
    {
      "type": "h2",
      "text": "Un rez-de-chaussée public, quatre étages à louer"
    },
    {
      "type": "p",
      "text": "La répartition des surfaces éclaire l'usage réel du lieu, et c'est là que se joue l'implantation physique de l'écosystème local. Le rez-de-chaussée de 792 m² est porté par Grand Besançon Métropole ; les 2 280 m² des quatre étages sont portés par Territoire 25 et destinés aux entreprises de la filière[^2]. L'offre locative en détaille le découpage :"
    },
    {
      "type": "ul",
      "items": [
        "**Rez-de-chaussée (792 m²)** : accueil, espaces de formation, FabLab ouvert au grand public et aux entreprises, salle immersive 360°[^2], espace événementiel de 200 à 250 m²[^3] ;",
        "**R+1** : coworking et occupation réduite, avec des contrats d'occupation flexibles « en tout compris », première marche du parcours résidentiel[^2][^3] ;",
        "**R+2** : bureaux privatifs de 15 à 50 m²[^3] ;",
        "**R+3** : plateaux modulables de 50 à 130 m²[^3] ;",
        "**R+4** : un grand plateau de 130 m² doublé d'une terrasse[^3]."
      ]
    },
    {
      "type": "p",
      "text": "Le principe assumé est celui d'un parcours : primo-accueil au rez-de-chaussée, passage par le coworking, puis installation durable en bureau ou en plateau dans les étages[^2]. La structure mixte bois-béton et le report des éléments techniques en partie commune permettent de déplacer les cloisons sans travaux structurels[^2]. Un atrium traverse le bâtiment sur toute sa hauteur, avec un escalier destiné à « provoquer les échanges entre les entreprises », selon l'architecte[^1]."
    },
    {
      "type": "h2",
      "text": "Une concession de vingt-cinq ans, pas un exploitant privé"
    },
    {
      "type": "p",
      "text": "Le mode de gestion n'est pas celui d'un exploitant tiers : le montage retenu est une concession de travaux et de services entre Grand Besançon Métropole, concédant, et Territoire 25, concessionnaire[^2]. Le rez-de-chaussée devient propriété de la métropole dès la livraison, en exploitation directe ; les étages restent propriété de Territoire 25 pendant les vingt-cinq ans de la concession et sont mis en location, à la métropole comme à des entreprises[^2]. Aucun nom d'exploitant extérieur n'a été avancé."
    },
    {
      "type": "p",
      "text": "L'animation s'appuiera sur des groupes experts mobilisés en amont et sur une promotion relayée par [la communauté French Tech](/blog/french-tech-bfc-capitale-2026-2028-besancon), l'AER BFC et Business France[^2]. La French Tech Bourgogne-Franche-Comté annonçait dès [la pose de la première pierre](/blog/premiere-pierre-le-numerique-planoise-besancon) qu'elle continuerait à travailler avec la métropole, Territoire 25 et l'ensemble des partenaires à préparer « l'animation, les usages et les futurs parcours entrepreneurs » du lieu[^6]. L'association Fabrique du Numérique à Besançon, présidée par Denis Puertolas, se dit pour sa part mobilisée pour préparer son arrivée[^1]."
    },
    {
      "type": "quote",
      "text": "une véritable vitrine des compétences du territoire, un showroom du savoir-faire numérique local",
      "cite": "La Fabrique du Numérique à Besançon, par la voix de son président Denis Puertolas[^1]"
    },
    {
      "type": "h2",
      "text": "Le remplissage : une offre locative en vitrine, pas encore de noms"
    },
    {
      "type": "p",
      "text": "La commercialisation, elle, a commencé. Une plaquette « offre locative » datée de mai 2026 annonce des loyers à partir de 120 € HT/m²/an, présentés comme un loyer prévisionnel moyen sur les 2 280 m²[^2][^3]. Bernard Bletton, directeur général délégué de Territoire 25, rappelle que ce niveau de loyer est rendu possible par les subventions de l'État (ANRU et ANRU+) et de la Région[^1][^2]. Les bureaux seront loués via des baux de moyenne durée, les plateaux via des baux longs, avec un bouquet de services inclus dans les charges et des prestations à la carte[^2]."
    },
    {
      "type": "p",
      "text": "En revanche, aucune liste d'entreprises candidates n'a été rendue publique, ni lors de la visite, ni dans le dossier de presse[^1][^2]. À douze mois de la livraison, le taux de pré-commercialisation reste l'inconnue centrale du dossier."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Le dossier de presse présente Le Numérique comme un outil de structuration de la filière numérique locale — animation, mise en réseau, soutien aux entreprises — à une entrée de quartier desservie par le tramway, face au [technopôle TEMIS Santé](/blog/temis-technopole-startups-deeptech-besancon)[^2]. C'est un pari d'agglomération autant qu'un pari immobilier : un lieu totem ne vaut que par ce qu'on y installe, et 2 280 m² d'espaces locatifs ne se remplissent pas d'eux-mêmes."
    },
    {
      "type": "h2",
      "text": "Ce qu'il reste à vérifier"
    },
    {
      "type": "p",
      "text": "Le chantier avance et la programmation est arrêtée dans le détail, du FabLab au plateau de R+4. Restent deux inconnues d'un autre ordre : la trajectoire de commercialisation des étages, et la manière dont la métropole exploitera concrètement son rez-de-chaussée public, qu'elle conservera en gestion directe[^2]. Les prochains mois — gros œuvre achevé mi-juillet, pose des façades à partir du mois d'août[^1] — donneront surtout à voir le bâtiment ; la réponse à la question du remplissage viendra plus tard."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Le Numérique : le futur totem de la tech' bisontine prend forme à Planoise",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/le-numerique-le-futur-totem-de-la-tech-bisontine-prend-forme-a-planoise/",
      "type": "press",
      "date": "2026-06-03"
    },
    {
      "id": 2,
      "title": "Dossier de presse « Le Numérique » — visite de chantier du 2 juin 2026",
      "publisher": "Grand Besançon Métropole et Territoire 25",
      "url": "https://www.grandbesancon.fr/wp-content/uploads/sites/2/2026/06/DP_LeNumerique_020626.pdf",
      "type": "official",
      "date": "2026-06-02"
    },
    {
      "id": 3,
      "title": "Le Numérique — offre locative (plaquette)",
      "publisher": "Grand Besançon Métropole et Territoire 25",
      "url": "https://www.grandbesancon.fr/wp-content/uploads/sites/2/2026/06/20260602_Plaquette-le-Numerique.pdf",
      "type": "official",
      "date": "2026-05-01"
    },
    {
      "id": 4,
      "title": "Un lieu totem du numérique en construction à Planoise",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/un-lieu-totem-du-numerique-en-construction-a-planoise/",
      "type": "official",
      "date": "2023-11-16"
    },
    {
      "id": 5,
      "title": "« Le Numérique » : un bâtiment de 3.000 m2 à 10,5 M€ bientôt au cœur de Planoise",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/le-numerique-un-batiment-de-3-000-m2-a-105-me-bientot-au-coeur-de-planoise/",
      "type": "press",
      "date": "2025-08-27"
    },
    {
      "id": 6,
      "title": "À Planoise, la première pierre du futur lieu totem numérique de Besançon est posée",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/actualites/ecosysteme/a-planoise-la-premiere-pierre-du-futur-lieu-totem-numerique-de-besancon-est-posee/",
      "type": "web",
      "date": "2025-08-27"
    }
  ]
};
